import { State } from '@meraki-internal/state';
import { CaremapNotesModelFactory } from '../model/CaremapNotesModelFactory';
import { ICaremapNoteResponse } from '../model/NotesClient';
import { CareMapState } from '../../caremap/CareMapState';
import { CareMapFactory } from '../../caremap/CareMapFactory';
import { DefaultAvatarProvider } from '../../avatar/DefaultAvatarProvider';
import { IHouseholdMember } from '../../caremap/ICareMap';
import { CommunityModelFactory } from '../../share-caremap/CommunityModelFactory';

type IShareNoteContact = IHouseholdMember & {
    id: string;
    addToHousehold?: boolean; // this is the checkbox for adding to household
};

type IShareNoteState = {
    note?: ICaremapNoteResponse;
    contacts: IShareNoteContact[]
};

export class ShareNoteState extends State<IShareNoteState> {
    static inject = () => [
        CareMapState,
        CareMapFactory,
        CaremapNotesModelFactory,
        DefaultAvatarProvider,
        CommunityModelFactory
    ];

    constructor(
        private careMapState: CareMapState,
        private careMapFactory: CareMapFactory,
        private notesModelFactory: CaremapNotesModelFactory,
        private defaultAvatarProvider: DefaultAvatarProvider,
        private communityModelFactory: CommunityModelFactory
    ) {
        super({ contacts: [] });
    };

    addContact = (contact: Omit<IShareNoteContact, 'id'>) => {
        const contacts = [
            ...this.state.contacts,
            {
                id: this.careMapFactory.getNodeId(),
                relationship: undefined,
                avatar: this.defaultAvatarProvider.getDefaultAvatar({
                    careMap: this.careMapState,
                    inUse: this.state.contacts.length > 0 ? this.state.contacts.map(c => c.avatar!) : undefined
                }),
                ...contact
            }
        ];
        this.setState({ contacts });
    };

    updateContact = (contactId: string, contact: IShareNoteContact) => {
        const contacts = this.state.contacts.map(c => c.id === contactId ? {...contact} : c);
        this.setState({ contacts });
    };

    getTotalHouseholdMemberCount = () => {
        return this.state.contacts.filter(c => c.addToHousehold).length + this.careMapState.getHouseholdMembers().length;
    };

    hasShared = () => {
        const community = this.communityModelFactory.forActiveCaremap();
        return community.isPermanentlyShared() || community.hasPendingInvites();
    };

    removeContact = (contactId: string) => {
        const contacts = this.state.contacts.filter(c => c.id !== contactId);
        this.setState({ contacts });
    };

    clearContacts = async () => {
        this.setState({ contacts: [] });
    };

    removeEmptyContacts = () => {
        const sanitizedContacts: IShareNoteContact[] = [];
        // loop through contacts
        this.state.contacts.forEach(contact => {
            // remove any contacts without firstName and lastName and contactMethods of type email
            if (contact.contactMethods && contact.contactMethods.some(cm => cm.type === 'email'
                && cm.value) && contact.firstName && contact.lastName) {
                sanitizedContacts.push(contact);
            }
        });

        this.setState({ contacts: sanitizedContacts });
    };

    makeNotePublic = async () => {
        if (this.state.note) {
            const noteVM = await this.notesModelFactory.createEditForm(this.state.note!.noteId);
            noteVM?.setNote({...this.state.note, ...{isPrivate: false}});
            noteVM?.save();
        }
    };

    private addToHousehold = async () => {
        const householdMembers = this.state.contacts.filter(c1 => c1.addToHousehold).map(c => {
            const { addToHousehold, ...rest } = c;
            return rest;
        });

        this.careMapState.addHouseholdMembers(householdMembers);
    };

    private inviteViewers = async () => {
        this.state.contacts.filter(c1 => !c1.addToHousehold).map(async contact => {
            await this.careMapState.getInviteLink({
                access: 'view-only',
                inviteeName: `${contact.firstName} ${contact.lastName}`,
                inviteeEmail: contact.contactMethods.find(cm => cm.type === 'email')?.value!,
                patientName: this.careMapState.getPatientFirstName()
            });
        });
    };

    shareWithContacts = async () => {
        this.removeEmptyContacts();
        await this.addToHousehold();
        await this.inviteViewers();
        await this.makeNotePublic();
        await this.clearContacts();
    };
};
